import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { StoryGenerationResult } from '../components/story-builder/StoryGenerationResult';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

// ── Types ───────────────────────────────────────────────────────────────────

interface SessionQuestion {
  id: string;
  sortOrder: number;
  response: string | null;
  skipped: boolean;
  question: {
    text: string;
    category: string;
  };
}

interface InterviewSession {
  id: string;
  status: string;
  questions: SessionQuestion[];
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// ── Component ───────────────────────────────────────────────────────────────

export function StoryBuilderInterviewPage() {
  const navigate = useNavigate();

  const [session, setSession] = useState<InterviewSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responseText, setResponseText] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showGeneration, setShowGeneration] = useState(false);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedTextRef = useRef<string>('');

  // ── Load or create session on mount ─────────────────────────────────────

  useEffect(() => {
    async function initSession() {
      try {
        setLoading(true);
        const sess = await apiClient<InterviewSession>('/story-builder/sessions', {
          method: 'POST',
        });
        setSession(sess);

        // Start at the first unanswered/unskipped question, or 0
        const firstUnanswered = sess.questions.findIndex(
          (q) => !q.response && !q.skipped
        );
        const startIdx = firstUnanswered >= 0 ? firstUnanswered : 0;
        setCurrentIndex(startIdx);
        setResponseText(sess.questions[startIdx]?.response || '');
        lastSavedTextRef.current = sess.questions[startIdx]?.response || '';

        // Check if all questions are already answered/skipped
        const allDone = sess.questions.every((q) => q.response || q.skipped);
        if (allDone && sess.questions.length > 0) {
          setShowConfirmation(true);
        }
      } catch (err: unknown) {
        const msg = err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to load interview session';
        setError(msg);
      } finally {
        setLoading(false);
      }
    }
    initSession();
  }, []);

  // ── Auto-save logic ─────────────────────────────────────────────────────

  const saveResponse = useCallback(
    async (text: string) => {
      if (!session) return;
      const trimmed = text.trim();
      if (!trimmed || trimmed === lastSavedTextRef.current?.trim()) return;

      setSaveStatus('saving');
      try {
        await apiClient(
          `/story-builder/sessions/${session.id}/responses/${currentIndex}`,
          { method: 'PATCH', body: { response: trimmed } }
        );
        setSaveStatus('saved');
        lastSavedTextRef.current = trimmed;

        // Update local session state
        setSession((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated.questions = [...updated.questions];
          updated.questions[currentIndex] = {
            ...updated.questions[currentIndex],
            response: trimmed,
            skipped: false,
          };
          return updated;
        });
      } catch {
        setSaveStatus('error');
      }
    },
    [session, currentIndex]
  );

  // Debounced auto-save on text change
  useEffect(() => {
    if (!responseText.trim()) return;
    if (responseText.trim() === lastSavedTextRef.current?.trim()) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveResponse(responseText);
    }, 1500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [responseText, saveResponse]);

  // ── Navigation handlers ─────────────────────────────────────────────────

  const navigateTo = (index: number) => {
    if (!session) return;

    // Save current response before navigating if changed
    const trimmed = responseText.trim();
    if (trimmed && trimmed !== lastSavedTextRef.current?.trim()) {
      saveResponse(responseText);
    }

    setCurrentIndex(index);
    setResponseText(session.questions[index]?.response || '');
    lastSavedTextRef.current = session.questions[index]?.response || '';
    setSaveStatus('idle');
  };

  const handleNext = () => {
    if (!session) return;
    if (currentIndex < session.questions.length - 1) {
      navigateTo(currentIndex + 1);
    } else {
      checkAllDone();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1);
    }
  };

  const handleSkip = async () => {
    if (!session) return;
    try {
      await apiClient(
        `/story-builder/sessions/${session.id}/skip/${currentIndex}`,
        { method: 'POST' }
      );
      setSession((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        updated.questions = [...updated.questions];
        updated.questions[currentIndex] = {
          ...updated.questions[currentIndex],
          skipped: true,
          response: null,
        };
        return updated;
      });
      setResponseText('');
      lastSavedTextRef.current = '';

      if (currentIndex < session.questions.length - 1) {
        setCurrentIndex(currentIndex + 1);
        const nextQ = session.questions[currentIndex + 1];
        setResponseText(nextQ?.response || '');
        lastSavedTextRef.current = nextQ?.response || '';
        setSaveStatus('idle');
      } else {
        checkAllDone();
      }
    } catch {
      setSaveStatus('error');
    }
  };

  const checkAllDone = () => {
    if (!session) return;
    // Re-check with latest session state
    setSession((prev) => {
      if (!prev) return prev;
      const allDone = prev.questions.every((q) => q.response || q.skipped);
      if (allDone) {
        setShowConfirmation(true);
      }
      return prev;
    });
  };

  // ── Submission ──────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!session) return;

    // Always try to generate — even if session status says story_generated,
    // the story might not exist (e.g., after DB reset). The API will either
    // generate fresh or return the existing story.
    setSubmitting(true);
    setShowConfirmation(false);
    setShowGeneration(true);
  };

  const handleReview = () => {
    setShowConfirmation(false);
    setCurrentIndex(0);
    if (session) {
      setResponseText(session.questions[0]?.response || '');
      lastSavedTextRef.current = session.questions[0]?.response || '';
    }
    setSaveStatus('idle');
  };

  // ── Computed values ─────────────────────────────────────────────────────

  const completedCount = session
    ? session.questions.filter((q) => q.response || q.skipped).length
    : 0;
  const answeredCount = session
    ? session.questions.filter((q) => q.response && !q.skipped).length
    : 0;
  const skippedCount = session
    ? session.questions.filter((q) => q.skipped).length
    : 0;
  const totalQuestions = session?.questions.length || 5;

  // ── Loading / Error states ──────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loadingState}>
            <div style={styles.spinner} />
            <p style={{ color: TEXT_MUTED, marginTop: '16px' }}>
              Loading your interview...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorState}>
            <span style={{ fontSize: '2rem' }}>⚠️</span>
            <p style={{ color: '#c62828', marginTop: '12px' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Story Generation View ───────────────────────────────────────────────

  if (showGeneration && session) {
    return (
      <div style={styles.page}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <StoryGenerationResult
          sessionId={session.id}
          onGenerationStart={() => {}}
          onStoryUpdate={() => {}}
          onApproved={() => navigate('/story-builder/my-story')}
          onDeclined={() => {
            setShowGeneration(false);
            setShowConfirmation(true);
          }}
        />
      </div>
    );
  }

  // ── Confirmation Screen ─────────────────────────────────────────────────

  if (showConfirmation) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <span style={{ fontSize: '3rem' }}>✨</span>
              <h2 style={{ ...styles.heading, marginTop: '16px' }}>
                Interview Complete
              </h2>
              <p style={{ color: TEXT_MUTED, marginTop: '12px', fontSize: '15px' }}>
                You've completed all 5 questions. Here's your summary:
              </p>
              <div style={styles.summaryBox}>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryNumber}>{answeredCount}</span>
                  <span style={styles.summaryLabel}>Answered</span>
                </div>
                <div style={styles.summaryDivider} />
                <div style={styles.summaryItem}>
                  <span style={styles.summaryNumber}>{skippedCount}</span>
                  <span style={styles.summaryLabel}>Skipped</span>
                </div>
              </div>
              {error && (
                <p style={{ color: '#c62828', fontSize: '13px', marginTop: '12px' }}>
                  {error}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '32px' }}>
                <button
                  style={styles.btnSecondary}
                  onClick={handleReview}
                >
                  Review Responses
                </button>
                <button
                  style={styles.btnPrimary}
                  onClick={handleSubmit}
                  disabled={submitting || answeredCount === 0}
                >
                  {submitting ? 'Submitting...' : 'Submit & Generate Story'}
                </button>
              </div>
              {answeredCount === 0 && (
                <p style={{ color: '#c62828', fontSize: '13px', marginTop: '12px' }}>
                  At least one response is required to generate a story.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Interview Question View ─────────────────────────────────────────────

  const currentQuestion = session?.questions[currentIndex];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.heading}>Story Builder Interview</h1>
          <p style={{ color: TEXT_MUTED, margin: '4px 0 0' }}>
            Share your stories and heritage connections
          </p>
        </div>

        {/* Progress indicator */}
        <div style={styles.progressSection}>
          <div style={styles.progressInfo}>
            <span style={{ color: TEXT_DARK, fontWeight: 600, fontSize: '14px' }}>
              Question {currentIndex + 1} of {totalQuestions}
            </span>
            <span style={{ color: TEXT_MUTED, fontSize: '13px' }}>
              {completedCount} of {totalQuestions} completed
            </span>
          </div>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${(completedCount / totalQuestions) * 100}%`,
              }}
            />
          </div>
          {/* Question dots */}
          <div style={styles.dots}>
            {session?.questions.map((q, idx) => (
              <button
                key={idx}
                style={{
                  ...styles.dot,
                  backgroundColor: q.response
                    ? PRIMARY
                    : q.skipped
                    ? TEXT_MUTED
                    : idx === currentIndex
                    ? '#fff'
                    : BORDER_LIGHT,
                  border: idx === currentIndex
                    ? `2px solid ${PRIMARY}`
                    : '2px solid transparent',
                }}
                onClick={() => navigateTo(idx)}
                aria-label={`Go to question ${idx + 1}${q.response ? ' (answered)' : q.skipped ? ' (skipped)' : ''}`}
                title={`Question ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Question card */}
        <div style={styles.card}>
          {/* Category badge */}
          <div style={styles.categoryBadge}>
            {currentQuestion?.question.category.replace(/([A-Z])/g, ' $1').trim()}
          </div>

          {/* Question text */}
          <h2 style={styles.questionText}>{currentQuestion?.question.text}</h2>

          {/* Text input area */}
          <div style={styles.textareaWrapper}>
            <textarea
              style={styles.textarea}
              value={responseText}
              onChange={(e) => {
                if (e.target.value.length <= 2000) {
                  setResponseText(e.target.value);
                  if (saveStatus === 'saved' || saveStatus === 'error') {
                    setSaveStatus('idle');
                  }
                }
              }}
              placeholder="Share your thoughts, memories, and stories..."
              maxLength={2000}
              rows={6}
              aria-label="Your response"
            />
            <div style={styles.textareaFooter}>
              <span style={{ color: TEXT_MUTED, fontSize: '12px' }}>
                {responseText.length} / 2000
              </span>
              {/* Save status indicator */}
              <span style={styles.saveIndicator}>
                {saveStatus === 'saving' && (
                  <span style={{ color: TEXT_MUTED, fontSize: '12px' }}>
                    Saving...
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span style={{ color: '#2e7d32', fontSize: '12px' }}>
                    ✓ Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span style={{ color: '#c62828', fontSize: '12px' }}>
                    ✕ Save failed — will retry
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Navigation buttons */}
          <div style={styles.navButtons}>
            <button
              style={{
                ...styles.btnSecondary,
                visibility: currentIndex > 0 ? 'visible' : 'hidden',
              }}
              onClick={handlePrevious}
            >
              ← Previous
            </button>
            <button
              style={styles.btnSkip}
              onClick={handleSkip}
            >
              Skip
            </button>
            <button
              style={styles.btnPrimary}
              onClick={handleNext}
            >
              {currentIndex < totalQuestions - 1 ? 'Next →' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
    padding: '32px 16px',
  },
  container: {
    maxWidth: '680px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  heading: {
    color: TEXT_DARK,
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${BORDER_LIGHT}`,
    borderTopColor: PRIMARY,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  progressSection: {
    marginBottom: '24px',
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  progressBar: {
    height: '6px',
    backgroundColor: BORDER_LIGHT,
    borderRadius: '3px',
    overflow: 'hidden' as const,
  },
  progressFill: {
    height: '100%',
    backgroundColor: PRIMARY,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  dots: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    marginTop: '12px',
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
  },
  categoryBadge: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: PRIMARY,
    backgroundColor: `${PRIMARY}14`,
    padding: '4px 10px',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  questionText: {
    color: TEXT_DARK,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.4,
    margin: '0 0 24px',
  },
  textareaWrapper: {
    marginBottom: '24px',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    lineHeight: 1.6,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    resize: 'vertical' as const,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
    minHeight: '150px',
  },
  textareaFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  saveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  navButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  btnPrimary: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    backgroundColor: '#f0f2f1',
    color: TEXT_DARK,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSkip: {
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  summaryBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    marginTop: '24px',
    padding: '24px',
    backgroundColor: BG_PAGE,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
  },
  summaryNumber: {
    fontSize: '28px',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  summaryLabel: {
    fontSize: '13px',
    color: TEXT_MUTED,
  },
  summaryDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: BORDER_LIGHT,
  },
};
